import React, { useState, useRef, useEffect } from 'react';
import { useTheme } from '@context/ThemeContext';
import { aiHelpService } from '@services/api/ai-help.service';

interface Message {
  id: string;
  type: 'user' | 'bot';
  content: string;
  timestamp: Date;
  confidence?: number;
  disclaimer?: boolean;
  recommendTicket?: boolean;
}

export interface HelpEscalateContext {
  sessionId?: string;
  message?: string;
}

interface HelpChatbotProps {
  isOpen: boolean;
  onClose: () => void;
  onEscalate: (context: HelpEscalateContext) => void;
}

const SUGGESTED_QUESTIONS = [
  'How do I reset my password?',
  'My live class video is not connecting',
  'How do I enroll in a new course?',
  'How do I contact support?',
];

export const HelpChatbot: React.FC<HelpChatbotProps> = ({ isOpen, onClose, onEscalate }) => {
  const { themeConfig } = useTheme();
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'bot',
      content: "Hello! 👋 I'm the Aahana SmartTutor Help Assistant. How can I help you today?",
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async (text: string) => {
    if (!text.trim() || isLoading) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: text,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const answer = await aiHelpService.chat(text, sessionId);
      setSessionId(answer.session_id);

      const botMessage: Message = {
        id: answer.message.id,
        type: 'bot',
        content: answer.message.content,
        timestamp: new Date(),
        confidence: answer.confidence,
        disclaimer: answer.disclaimer,
        // auto_open_ticket used to close the chat and jump to the ticket form
        // before the user could read the reply. Keep the chat open and surface
        // the in-message "create ticket" action instead.
        recommendTicket: answer.recommend_ticket || answer.auto_open_ticket,
      };
      setMessages((prev) => [...prev, botMessage]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 1).toString(),
          type: 'bot',
          content: "Sorry, I couldn't reach the assistant right now. You can create a support ticket instead.",
          timestamp: new Date(),
          recommendTicket: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    await sendMessage(inputValue);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className={`fixed bottom-0 right-0 w-full sm:w-96 h-full sm:h-[600px] ${themeConfig.layout.contentBackground} rounded-t-2xl sm:rounded-2xl shadow-2xl z-50 flex flex-col`}>
        {/* Header */}
        <div className={`${themeConfig.accent.primary} text-white p-4 rounded-t-2xl sm:rounded-t-2xl flex items-center justify-between`}>
          <div className="flex items-center gap-3">
            <span className="text-2xl">🤖</span>
            <div>
              <h3 className="font-bold text-lg">Help & Support</h3>
              <p className="text-xs opacity-90">AI Assistant</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            aria-label="Close help"
          >
            ✕
          </button>
        </div>

        {/* Messages Area */}
        <div className={`flex-1 overflow-y-auto p-4 space-y-3 ${themeConfig.layout.contentBackground}`}>
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs px-4 py-2 rounded-lg ${
                  message.type === 'user'
                    ? `${themeConfig.accent.primary} text-white rounded-br-none`
                    : `${themeConfig.sidebar.background} ${themeConfig.sidebar.text} rounded-bl-none`
                }`}
              >
                <p className="text-sm">{message.content}</p>
                {message.disclaimer && (
                  <p className="text-xs mt-1 italic opacity-75">
                    ⚠️ This answer may not be fully accurate — please verify.
                  </p>
                )}
                <p className={`text-xs mt-1 ${message.type === 'user' ? 'opacity-75' : themeConfig.sidebar.textTertiary}`}>
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
                {message.recommendTicket && (
                  <button
                    onClick={() => onEscalate({ sessionId, message: messages.filter(m => m.type === 'user').slice(-1)[0]?.content })}
                    className={`mt-2 w-full text-xs py-1.5 px-2 rounded-md border ${themeConfig.sidebar.border} font-semibold hover:opacity-80 transition-opacity`}
                  >
                    🎫 Create Support Ticket
                  </button>
                )}
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="flex justify-start">
              <div className={`${themeConfig.sidebar.background} ${themeConfig.sidebar.text} px-4 py-2 rounded-lg rounded-bl-none`}>
                <span className="flex items-center gap-2">
                  <span className="animate-bounce">●</span>
                  <span className="animate-bounce" style={{ animationDelay: '0.2s' }}>●</span>
                  <span className="animate-bounce" style={{ animationDelay: '0.4s' }}>●</span>
                </span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        {messages.length <= 1 && (
          <div className={`px-4 py-3 border-t ${themeConfig.sidebar.border} space-y-2`}>
            <p className={`text-xs font-semibold ${themeConfig.sidebar.textTertiary}`}>Common Questions</p>
            <div className="grid grid-cols-2 gap-2">
              {SUGGESTED_QUESTIONS.map((question, idx) => (
                <button
                  key={idx}
                  onClick={() => sendMessage(question)}
                  className={`text-xs p-2 rounded-lg ${themeConfig.sidebar.hoverBg} ${themeConfig.sidebar.text} hover:opacity-80 transition-opacity text-left line-clamp-2`}
                >
                  {question}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className={`border-t ${themeConfig.sidebar.border} p-4 space-y-3`}>
          <form id="help-chatbot-form" onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder="Ask a question..."
              disabled={isLoading}
              className={`flex-1 px-3 py-2 rounded-lg border ${themeConfig.sidebar.border} focus:outline-none focus:ring-2 ${themeConfig.accent.primary} bg-opacity-20 disabled:opacity-50`}
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className={`${themeConfig.accent.primary} text-white px-4 py-2 rounded-lg font-semibold disabled:opacity-50 hover:opacity-90 transition-opacity`}
            >
              Send
            </button>
          </form>

          <button
            onClick={() => onEscalate({ sessionId, message: messages.filter(m => m.type === 'user').slice(-1)[0]?.content })}
            className={`w-full py-2 px-3 rounded-lg border-2 ${themeConfig.accent.primaryText} border-current text-sm font-semibold hover:opacity-80 transition-opacity`}
          >
            📞 Create Support Ticket
          </button>
        </div>
      </div>
    </>
  );
};
